import { store } from '@/app/store'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { useAppDispatch } from '@/app/reduxHooks'
import { mergeAdpSessionNode } from './useAdpSessionStore'
import { useTargetData } from './useTargetData'
import type { RegisteredDatabase } from '@/features/adp/services/api-service.types'
import type { PersistedDestinationConfigurationState } from '@/features/adp/components/destination/sheets/DestinationConfiguration'

/**
 * Bridge for the "+ New Table" path on a registered destination. Per ADP
 * Swagger, POST `/target-tables/batch` accepts `data_source_name` so a new
 * target table can be created under an existing target_data_source. The
 * existing OutputPreview flow already calls that endpoint — this hook
 * pre-populates the session with the registered destination's identity
 * (without `target_table`) and routes the user to LoadType → OutputPreview,
 * which will POST `target-tables/batch` against the existing destination.
 *
 * Mirrors what DestinationConfiguration writes on first-save, minus the
 * `useCreateTargetDataSourceMutation` step (the data source already exists).
 */
const REJECTED_PAYLOAD_KEYS = new Set(['database_name', 'endpoint', 'secret_key2'])

function buildSubmittedDestinationPayload(
  database: RegisteredDatabase,
  tableName: string,
): Record<string, unknown> {
  const raw = database as unknown as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(raw)) {
    if (REJECTED_PAYLOAD_KEYS.has(k)) continue
    if (v === '' || v === null || v === undefined) continue
    if (k === 'image_link' || k === 'data_source_instance_label' || k === 'data_source_type') continue
    out[k] = v
  }
  if (database.port) {
    const port = Number(database.port)
    if (Number.isFinite(port) && port > 0) {
      out.port = port
    }
  }
  out.type = database.type
  out.source_name = database.source_name
  // Per ADP Swagger, PhysicalTableDatabaseDto requires `table_name` INSIDE
  // each `databases[]` object. `getDestinationDatabaseRecordForPhysicalTable`
  // returns this payload verbatim and `buildPhysicalTableCreatePayload`
  // spreads it into `databases[0]` — so the new table name has to live here.
  out.table_name = tableName
  return out
}

export interface StartNewTableOnRegisteredDestinationParams {
  nodeId: string
  targetDataSourceId: number
  database: RegisteredDatabase
  /** Name for the new target table the user is about to design in Output Preview. */
  tableName: string
}

export function useStartNewTableOnRegisteredDestination() {
  const { setTargetData } = useTargetData()
  const dispatch = useAppDispatch()

  return (params: StartNewTableOnRegisteredDestinationParams) => {
    const { nodeId, targetDataSourceId, database, tableName } = params
    const dataSourceInstance = database.type ?? ''
    const dataSourceType = database.data_source_type ?? null

    // Seed target data with the destination identity but NO `target_table`.
    // OutputPreview's `isAlreadyCreated` is keyed on target_table.length > 0,
    // so we deliberately leave it absent — the full create flow runs.
    // `table_name` IS set: OutputPreview's `isCreateTargetResponseCandidate`
    // requires source_name + table_name + path, and there's no
    // DestinationConfiguration step in this flow to supply the name.
    setTargetData(nodeId, {
      target_data_source_id: targetDataSourceId,
      source_name: database.source_name,
      table_name: tableName,
      path: '',
      database: database.database,
      schema: database.schema || null,
      data_source_instance: dataSourceInstance,
      data_source_type: dataSourceType,
    })

    const submittedDestinationPayload = buildSubmittedDestinationPayload(database, tableName)
    mergeAdpSessionNode(nodeId, {
      destinationConfigurationState: {
        isConfigured: true,
        isReadOnly: true,
        values: {
          source_name: database.source_name ?? '',
          table_name: tableName,
          host: database.host ?? '',
          port: database.port ?? '',
          database: database.database ?? '',
          username: database.username ?? '',
          password: database.password ?? '',
          schema: database.schema ?? '',
          secret_key: database.secret_key ?? '',
        },
        schemaFields: [],
        postTestFields: [],
        submitted_destination_payload:
          submittedDestinationPayload as PersistedDestinationConfigurationState['submitted_destination_payload'],
        data_source_instance: dataSourceInstance,
        data_source_type: dataSourceType,
      } satisfies PersistedDestinationConfigurationState,
    })

    const snapshot = store.getState()
    const nextNodes = [...snapshot.canvas.present.nodes]
    const destinationNodeIndex = nextNodes.findIndex(
      node => node.id === nodeId && node.type === 'destination',
    )
    if (destinationNodeIndex >= 0) {
      const destinationNode = nextNodes[destinationNodeIndex]
      nextNodes[destinationNodeIndex] = {
        ...destinationNode,
        data: {
          ...destinationNode.data,
          nodeSecondaryTitle: database.source_name ?? destinationNode.data?.nodeSecondaryTitle,
          mode: 'existing-destination',
        },
      } as typeof destinationNode
      dispatch(
        syncCanvasState({
          nodes: nextNodes,
          edges: snapshot.canvas.present.edges,
        }),
      )
    }

    // Skip DestinationConfiguration (already marked configured) and jump
    // straight to LoadType. From there the existing flow handles the rest.
    dispatch(
      openOverlay({
        type: 'load-type',
        data: { nodeId },
      }),
    )
  }
}
